import { useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@context';
import { useAuthHttp } from '@hooks';
import { RESEND_CONFIRMATION_URL } from '@constants';

/**
 * Holly 3.0 Registration spec §8.2 + §6 — soft banner shown on destination pages
 * when the user's email is not yet verified. Soft tone (informational, dismissible)
 * for the first 14 days; the §8 spec calls for a blocking banner past that
 * threshold which is wired up in Phase 4.4.
 */
export function EmailVerificationBanner() {
  const { user } = useAuth();
  const authHttp = useAuthHttp();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  if (!user || (user as { isEmailConfirmed?: boolean }).isEmailConfirmed || dismissed) {
    return null;
  }

  const handleResend = async () => {
    setResending(true);
    try {
      await authHttp.post(RESEND_CONFIRMATION_URL, {
        email: (user as { email?: string }).email,
        baseUrl: window.location.origin,
      });
      setResent(true);
    } catch {
      // Best-effort UX — backend returns 200 even when the email is unrecognised
      // (anti-enumeration); a real failure is rare.
      setResent(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className='flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900'>
      <ExclamationTriangleIcon className='w-5 h-5 shrink-0 mt-0.5' />
      <div className='flex-1'>
        <strong>Verify your email</strong> to unlock messaging, quote requests, and
        marketplace publishing.{' '}
        {resent ? (
          <span className='font-semibold'>Verification email sent. Check your inbox.</span>
        ) : (
          <button
            type='button'
            onClick={handleResend}
            disabled={resending}
            className='underline font-semibold hover:no-underline disabled:opacity-60'
          >
            {resending ? 'Sending…' : 'Resend verification'}
          </button>
        )}
      </div>
      <button
        type='button'
        onClick={() => setDismissed(true)}
        aria-label='Dismiss'
        className='shrink-0 text-amber-700 hover:text-amber-900'
      >
        <XMarkIcon className='w-5 h-5' />
      </button>
    </div>
  );
}
